import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  TableCell,
  Paper,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import DateConverter from "../../../utils/DateConverter";
import {
  PrimaryColor,
  TableBodyGrey,
  TableHeaderGrey,
  TableRowHover,
} from "../../../shared/styles/colors";
import { DatabaseEntities } from "../../../model/enum/database-entities.enum";
import { Typography, Button } from "@material-ui/core";
import { TablePagination } from "@mui/material";
import { Spinner } from "../../../components";
import { ToastContainer } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import ApiService from "../../../service/ApiService";
import ProductModel from "../../../model/product";
import ProductDetailModal from "./ProductDetailModal";
import CachedIcon from "@mui/icons-material/Cached";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useStyles from "../../styles/cardStyles";
import Pathnames from "../../../model/enum/Pathnames";
import PageResultResponse from "../../../model/page-result-response";
import Misc from "../../../utils/Misc";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: TableHeaderGrey,
    color: PrimaryColor,
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 0,
    position: "static",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: TableBodyGrey,
    fontFamily: "monospace",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: TableRowHover,
  },
}));

const Products = () => {
  const classes = useStyles();
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.searchParams.set("rowsPerPage", rowsPerPage.toString());
    window.history.pushState({}, Pathnames.PRODUCTS_SERVICES_LIST, url);
  });

  // Get all the products items from the database
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("rowsPerPage", rowsPerPage.toString());

      await ApiService.get<PageResultResponse>(
        `${DatabaseEntities.PRODUCTS}?${queryParams.toString()}`,
      ).then((data) => {
        if (Misc.isArrayEmpty(data.items)) return;
        const { items, count } = data;
        setProducts(items);
        setTotalPages(count);
      });
      setLoading(false);
    };

    if (loading && Misc.isArrayEmpty(products)) fetchData();
  }, [products]);

  const resetList = () => {
    setPage(1);
    setRowsPerPage(10);
    setProducts([]);
    setLoading(true);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown> | null,
    pg: number,
  ) => {
    setPage(pg + 1);
    scrollToTop();
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    let row = parseInt(event.target.value);
    setRowsPerPage(row);
    scrollToTop();
  };

  const scrollToTop = () => {
    setProducts([]);
    setLoading(true);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const addIdToQueryPath = (id?: number) => {
    if (!id) return;

    const url = new URL(window.location.href);
    url.searchParams.set("productId", id.toString());
    window.history.pushState({}, Pathnames.PRODUCTS_SERVICES_LIST, url);
  };

  const removeIdFromQueryPath = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("productId");
    window.history.replaceState({}, "", url.pathname + url.search);
  };

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.title}></Typography>
        <div className={classes.headerDiv}>
          <Button onClick={resetList}>
            <CachedIcon />
          </Button>
        </div>
      </div>
      <Paper sx={{ marginTop: "20px" }}>
        <TableContainer>
          <Table
            stickyHeader
            aria-label="customized table"
            padding="normal"
            size="medium"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell align="right">Id</StyledTableCell>
                <StyledTableCell align="right">Name</StyledTableCell>
                <StyledTableCell align="right">Category</StyledTableCell>
                <StyledTableCell align="right">Description</StyledTableCell>
                <StyledTableCell align="right">Type</StyledTableCell>
                <StyledTableCell align="right">Vendor</StyledTableCell>
                <StyledTableCell align="right">Cost</StyledTableCell>
                <StyledTableCell align="right">Price</StyledTableCell>
                <StyledTableCell align="right">Created At</StyledTableCell>
                <StyledTableCell align="right">
                  <MoreVertIcon />
                </StyledTableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <Spinner />
            ) : (
              <TableBody>
                {products.length > 0 ? (
                  products.map((row: ProductModel) => (
                    <StyledTableRow key={row.id}>
                      <StyledTableCell align="right">{row.id}</StyledTableCell>
                      <StyledTableCell align="right">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.Category?.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <span className={classes.limitedText}>
                          {row.description}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.isProduct ? "Product" : "Service"}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.Vendor?.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.cost ? `$${row.cost}` : ""}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        ${row.price}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {DateConverter.toFullDate(row.createdAt)}
                      </StyledTableCell>
                      <StyledTableCell>
                        <ProductDetailModal
                          product={row}
                          resetList={resetList}
                          onOpen={() => {
                            addIdToQueryPath(row?.id);
                          }}
                          onClose={() => {
                            removeIdFromQueryPath();
                          }}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableCell align="right">No items</StyledTableCell>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
      <TablePagination
        component="div"
        count={totalPages}
        page={page - 1}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ToastContainer />
    </>
  );
};

export default Products;
